import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material'
import {
  Search,
  School,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../app/store'
import { fetchCourses, fetchUserEnrollments, enrollInCourse } from '../features/courses/courseSlice'
import { fetchUserEnrollments as fetchAssignmentEnrollments } from '../features/assignments/assignmentSlice'
import { Course, Enrollment, UserEnrollment } from '../types'
import WelcomeCard from '../components/WelcomeCard'

const ModernCourseCard: React.FC<{ 
  course: Course
  enrollment?: Enrollment | UserEnrollment
  onEnroll: (courseId: number) => Promise<void>
  onContinue: (courseId: number) => void
  isEnrolling: boolean
}> = ({ course, enrollment, onEnroll, onContinue, isEnrolling }) => {
  const theme = useTheme()

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return theme.palette.success.main
      case 'intermediate': return theme.palette.warning.main
      case 'advanced': return theme.palette.error.main
      default: return theme.palette.grey[500]
    }
  }

  return (
    <Card
      sx={{
        height: 100,
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Left Icon Section */}
      <Box
        sx={{
          width: 80,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <School sx={{ fontSize: 28, color: 'white' }} />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: 1, position: 'relative' }}>
        {/* Title and Description */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="bold" 
            sx={{
              fontSize: '0.95rem',
              lineHeight: 1.2,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {course.title}
          </Typography>

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.5,
            }}
          >
            {course.description}
          </Typography>
        </Box>

        {/* Bottom Row - Chips and Button */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={course.difficulty}
              size="small"
              sx={{
                bgcolor: getDifficultyColor(course.difficulty) + '20',
                color: getDifficultyColor(course.difficulty),
                fontWeight: 600,
                fontSize: '0.6rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            <Chip
              label={course.type}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: 18,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          </Box>

          {/* Action Button */}
          {enrollment ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => onContinue(course.id)}
              disabled={enrollment.status === 'completed'}
              sx={{
                borderRadius: '4px',
                px: 2,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
                minWidth: 'auto',
                height: 26,
              }}
            >
              {enrollment.status === 'completed' ? 'Done' : 'Continue'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => onEnroll(course.id)}
              disabled={isEnrolling}
              sx={{
                borderRadius: '4px',
                px: 2,
                py: 0.5,
                fontSize: '0.7rem',
                textTransform: 'none',
                minWidth: 'auto',
                height: 26,
              }}
            >
              {isEnrolling ? 'Loading...' : 'Start'}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}

const ModernCourseCatalogPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const theme = useTheme()
  const { courses, enrollments, isLoading, error } = useSelector((state: RootState) => state.courses)
  const { user } = useSelector((state: RootState) => state.auth)
  
  // Also get user enrollments from assignments slice since that's where the enrollment data is
  // Backend now returns only current user's enrollments via only_mine=true parameter
  const { userEnrollments } = useSelector((state: RootState) => state.assignments)

  const [searchTerm, setSearchTerm] = useState('')
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [enrollingCourseId, setEnrollingCourseId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(fetchCourses())
    // Fetch from BOTH slices to populate both enrollments arrays
    dispatch(fetchUserEnrollments())  // Populates state.courses.enrollments
    dispatch(fetchAssignmentEnrollments())  // Populates state.assignments.userEnrollments
  }, [dispatch])

  const handleEnroll = async (courseId: number) => {
    try {
      setEnrollingCourseId(courseId)
      await dispatch(enrollInCourse(courseId.toString())).unwrap()
      // Refresh enrollments after successful enrollment from BOTH slices
      dispatch(fetchUserEnrollments())
      dispatch(fetchAssignmentEnrollments())
    } catch (error) {
      console.error('Enrollment failed:', error)
    } finally {
      setEnrollingCourseId(null)
    }
  }

  const handleContinue = (courseId: number) => {
    navigate(`/courses/${courseId}`)
  }

  // Filter courses (provide fallback empty array)
  const filteredCourses = (courses || []).filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesDifficulty = difficultyFilter === 'all' || course.difficulty === difficultyFilter
    const matchesType = typeFilter === 'all' || course.type === typeFilter
    
    return matchesSearch && matchesDifficulty && matchesType
  })

  // Get enrollment for a course
  const getEnrollmentForCourse = (courseId: number) => {
    // Backend already returns only current user's enrollments via only_mine=true parameter
    const assignmentEnrollments = (userEnrollments || []).filter(ue => 
      ue.assignment?.course && 
      (typeof ue.assignment.course === 'object' 
        ? ue.assignment.course.id === courseId
        : Number(ue.assignment.course) === courseId)
    )

    if (assignmentEnrollments.length === 0) return undefined
    
    // Sort by progress (descending) to show the most progressed enrollment
    return assignmentEnrollments.sort((a, b) => {
      const progressA = Number(a.progress) || 0
      const progressB = Number(b.progress) || 0
      
      if (progressA !== progressB) return progressB - progressA
      
      // If progress is same, prefer completed/in_progress over assigned/not_started
      const getStatusScore = (status: string) => {
        if (status === 'completed') return 3
        if (status === 'in_progress') return 2
        return 1
      }
      
      const scoreA = getStatusScore(a.status)
      const scoreB = getStatusScore(b.status)
      
      if (scoreA !== scoreB) return scoreB - scoreA
      
      // If everything else is equal, prefer the one with a more recent ID (likely newer)
      // Handle cases where id might be string or number
      const idA = Number(a.id) || 0
      const idB = Number(b.id) || 0
      return idB - idA
    })[0]
  }

  const totalCourses = (courses || []).length
  const enrolledCourses = (userEnrollments || []).filter(ue => ue.assignment?.course).length
  const completedCourses = (userEnrollments || []).filter(ue => ue.status === 'completed' && ue.assignment?.course).length

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Card */}
      <Box mb={3}>
        <WelcomeCard userName={user?.first_name || 'Student'} showLogo={true} />
      </Box>

      {/* Header Section */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Course Catalog
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Discover and enroll in courses to enhance your skills and knowledge
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="primary.main">
                  {totalCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Courses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="info.main">
                  {enrolledCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enrolled Courses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), border: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="success.main">
                  {completedCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Courses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                label="Difficulty"
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="external">External</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {/* Available Courses Section */}
      {(() => {
        // Only show courses under "Available" if the user actually has an enrollment
        // (either assignment-based or direct). Previously we considered "no enrollment"
        // as available which caused unassigned learners to see courses — that's wrong.
        // A course is "Available" if: has enrollment, status is assigned/not_started, AND progress is 0
        const availableCourses = filteredCourses.filter(course => {
          const enrollment = getEnrollmentForCourse(course.id)
          // Must have an enrollment and be in the assigned/not-started state with no progress
          const isAvailable = Boolean(enrollment) && (enrollment.status === 'assigned' || enrollment.status === 'not_started') && (Number(enrollment.progress) || 0) === 0
          return isAvailable
        })
        
        return (
          <Box mb={4}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: theme.palette.info.main }}>
              Available Courses ({availableCourses.length})
            </Typography>
            {availableCourses.length > 0 ? (
              <Grid container spacing={3}>
                {availableCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} key={course.id}>
                    <ModernCourseCard
                      course={course}
                      enrollment={getEnrollmentForCourse(course.id)}
                      onEnroll={handleEnroll}
                      onContinue={handleContinue}
                      isEnrolling={enrollingCourseId === course.id}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No available courses found.
              </Typography>
            )}
          </Box>
        )
      })()}

      {/* In Progress Courses Section */}
      {(() => {
        const inProgressCourses = filteredCourses.filter(course => {
          const enrollment = getEnrollmentForCourse(course.id)
          const progress = Number(enrollment?.progress) || 0
          // A course is "In Progress" if:
          // 1. Status is explicitly 'in_progress', OR
          // 2. Has any progress (>0) but not yet completed (<100)
          // This ensures courses show correctly even if status hasn't been updated yet
          const isInProgress = enrollment && 
                 (enrollment.status === 'in_progress' || 
                 (progress > 0 && progress < 100))
          return isInProgress
        })
        
        return (
          <Box mb={4}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: theme.palette.primary.main }}>
              In Progress Courses ({inProgressCourses.length})
            </Typography>
            {inProgressCourses.length > 0 ? (
              <Grid container spacing={3}>
                {inProgressCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} key={course.id}>
                    <ModernCourseCard
                      course={course}
                      enrollment={getEnrollmentForCourse(course.id)}
                      onEnroll={handleEnroll}
                      onContinue={handleContinue}
                      isEnrolling={enrollingCourseId === course.id}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No courses in progress found.
              </Typography>
            )}
          </Box>
        )
      })()}

      {/* Completed Courses Section */}
      {(() => {
        const completedCourses = filteredCourses.filter(course => {
          const enrollment = getEnrollmentForCourse(course.id)
          const isCompleted = enrollment && 
                 (enrollment.status === 'completed' || (Number(enrollment.progress) || 0) === 100)
          return isCompleted
        })
        
        return (
          <Box mb={4}>
            <Typography variant="h5" fontWeight="bold" mb={2} sx={{ color: theme.palette.success.main }}>
              Completed Courses ({completedCourses.length})
            </Typography>
            {completedCourses.length > 0 ? (
              <Grid container spacing={3}>
                {completedCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} key={course.id}>
                    <ModernCourseCard
                      course={course}
                      enrollment={getEnrollmentForCourse(course.id)}
                      onEnroll={handleEnroll}
                      onContinue={handleContinue}
                      isEnrolling={enrollingCourseId === course.id}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No completed courses found.
              </Typography>
            )}
          </Box>
        )
      })()}

      {filteredCourses.length === 0 && (
        <Box textAlign="center" py={8}>
          <School sx={{ fontSize: 80, color: theme.palette.grey[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No courses found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or filters
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ModernCourseCatalogPage